import React, {useState, useEffect} from 'react';
import Card from '../../../../Presentation/components/ui/Card';
import {AppText, AppTitle, AppCardTitle, AppBodyText, AppLabelText, AppSmallText} from '../../../../Presentation/components/ui/Typography';
import {Badge} from '../../../../Presentation/components/ui/Badge';
import Button from '../../../../Presentation/components/ui/Button';
import {DashboardUser} from '../../models/user.model';
import {CreateRoleModal} from '../modals/CreateRoleModal';
import {RolePermissionsModal} from '../modals/RolePermissionsModal';
import {useUserManagement} from '../../hooks/useUserManagement';
import {useNotification} from '../../../../Services/useNotification';
import {useAuthorization} from '../../../auth/hooks/useAuthorization';
import {PERMISSIONS} from '../../../../constants/permissions';
import {formatDate} from '../../../../Presentation/utils/dateUtils';
import {
  UserCog,
  Plus,
  Trash2,
  Shield,
  Users,
  RefreshCw
} from 'lucide-react';

interface RolesManagementProps {
  users: DashboardUser[];
  onAssignPermissions?: (userId: number, permissions: string[]) => Promise<void>;
}

export const RolesManagement: React.FC<RolesManagementProps> = ({
  users: _users,
  onAssignPermissions: _onAssignPermissions
}) => {
  const {hasPermission} = useAuthorization();
  const {success: showSuccess, error: showError} = useNotification();
  const {
    // UI State
    managingPermissionsRole,
    setManagingPermissionsRole,

    // Modal States
    isPermissionsModalOpen,

    // Modal Actions
    openPermissionsModal,
    closePermissionsModal,

    // Data
    roles,

    // Loading States
    rolesLoading: loading,
    rolesCreating: creating,
    rolesUpdating: updating,
    rolesDeleting: deleting,

    // Error States
    rolesError: error,

    // Actions
    loadRoles,
    handleCreateRole,
    handleDeleteRole,

    // Clear Actions
    clearRolesError: clearError
  } = useUserManagement();

  // Create modal state
  const [isCreateRoleModalOpen, setIsCreateRoleModalOpen] = useState(false);

  // Load roles when component mounts
  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  const filteredRoles = roles || [];


  const getRoleDisplayName = (roleName: string): string => {
    // Use dynamic role data from API instead of hardcoded mappings
    const role = roles.find(r => r.name === roleName);
    return role?.displayName || role?.name || roleName;
  };

  const getRoleBadgeVariant = (roleName: string): 'success' | 'warning' | 'destructive' | 'default' | 'secondary' => {
    // Use dynamic role data from API instead of hardcoded mappings
    const role = roles.find(r => r.name === roleName);

    // Use role priority or type to determine badge variant
    if (role?.priority === 'high' || roleName === 'admin' || roleName === 'super_admin') {
      return 'destructive';
    } else if (role?.priority === 'medium' || roleName.includes('manager')) {
      return 'warning';
    } else if (role?.priority === 'low' || roleName.includes('provider')) {
      return 'success';
    } else if (roleName.includes('viewer') || roleName.includes('guest')) {
      return 'default';
    }

    return 'secondary';
  };


  const handleDeleteRoleSuccess = async (roleId: number) => {
    // Prevent multiple clicks
    if (deleting) {return;}

    const role = (roles || []).find(r => r.id === roleId);
    if (role?.isSystem) {
      showError('لا يمكن حذف الأدوار النظامية');
      return;
    }

    if (window.confirm('هل أنت متأكد من حذف هذا الدور؟')) {
      try {
        await handleDeleteRole(roleId);
        showSuccess('تم حذف الدور بنجاح');
      } catch (error) {
        // Error notification is handled automatically by StandardizedErrorHandler
        // No need to manually show additional notifications
      }
    }
  };

  const handleCreateRoleClick = () => {
    setIsCreateRoleModalOpen(true);
  };

  const handleCloseCreateRoleModal = () => {
    setIsCreateRoleModalOpen(false);
  };

  const handleCreateRoleSubmit = async (roleData: {
    name: string;
    displayName: string;
    description: string;
  }) => {
    try {
      await handleCreateRole({
        ...roleData,
        permissions: [] // Add empty permissions array
      });
      showSuccess('تم إنشاء الدور بنجاح');
      handleCloseCreateRoleModal();
    } catch (error) {
      // Error notification is handled automatically by StandardizedErrorHandler
      // No need to manually show additional notifications
    }
  };

  const handleAssignPermissions = (roleId: number) => {
    const role = (roles || []).find(r => r.id === roleId);
    if (role) {
      setManagingPermissionsRole(role);
      openPermissionsModal(role);
    }
  };

  const handlePermissionsUpdated = () => {
    // Refresh roles to get updated permissions
    loadRoles();
  };


  return (
    <div className="space-y-6 flex-1 min-h-[calc(100vh-200px)] flex flex-col">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <AppCardTitle className="mb-2">إدارة الأدوار</AppCardTitle>
          <AppText className="text-gray-600">
            إدارة أدوار المستخدمين وصلاحياتهم في النظام
          </AppText>
        </div>
        <div className="flex space-x-3">
          <Button
            variant="outline"
            onClick={() => loadRoles()}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            تحديث
          </Button>
          <Button
            variant="primary"
            onClick={handleCreateRoleClick}
            disabled={creating}
          >
            <Plus className="w-4 h-4 mr-2" />
            إضافة دور جديد
          </Button>
        </div>
      </div>

      {/* Error is handled by StandardizedErrorHandler and shown as toast notification */}


      {/* Loading State */}
      {loading && (
        <Card className="p-12 text-center">
          <RefreshCw className="w-8 h-8 text-brand-500 mx-auto mb-4 animate-spin" />
          <AppText className="text-gray-600">جاري تحميل الأدوار...</AppText>
        </Card>
      )}

      {/* Roles Grid */}
      {!loading && filteredRoles.length > 0 && (
        <div className="flex-1 overflow-y-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredRoles.map((role) => (
          <Card key={role.id} className="p-6 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-brand-100 rounded-full">
                  <UserCog className="w-5 h-5 text-brand-600" />
                </div>
                <div>
                  <AppCardTitle>{role.displayName || getRoleDisplayName(role.name)}</AppCardTitle>
                  <Badge
                    variant={getRoleBadgeVariant(role.name)}
                    className="text-xs"
                  >
                    {role.name}
                  </Badge>
                </div>
              </div>
              {role.isSystem && (
                <Badge variant="warning" className="text-xs">
                  نظام
                </Badge>
              )}
            </div>

            <AppBodyText className="text-gray-600 mb-4">
              {role.description || 'لا يوجد وصف متاح'}
            </AppBodyText>

            <div className="space-y-3 mb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Users className="w-4 h-4 text-gray-500" />
                  <AppLabelText className="text-gray-600">المستخدمون</AppLabelText>
                </div>
                <AppText className="font-medium">{role.userCount}</AppText>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Shield className="w-4 h-4 text-gray-500" />
                  <AppLabelText className="text-gray-600">الصلاحيات</AppLabelText>
                </div>
                <AppText className="font-medium">{role.permissions.length}</AppText>
              </div>

              <div className="flex items-center justify-between">
                <AppLabelText className="text-gray-600">تاريخ الإنشاء</AppLabelText>
                <AppBodyText className="font-medium">{formatDate(role.createdAt)}</AppBodyText>
              </div>
            </div>

            {/* Permission Tags */}
            <div className="mb-4">
              <AppSmallText className="text-gray-500 mb-2">الصلاحيات:</AppSmallText>
              <div className="flex flex-wrap gap-1">
                {role.permissions.slice(0, 3).map((permission, index) => (
                  <Badge key={index} variant="secondary" className="text-xs">
                    {permission}
                  </Badge>
                ))}
                {role.permissions.length > 3 && (
                  <Badge variant="secondary" className="text-xs">
                    +{role.permissions.length - 3} أخرى
                  </Badge>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="space-y-2">
              <Button
                variant="primary"
                size="small"
                onClick={() => handleAssignPermissions(role.id)}
                className="w-full"
                permission={PERMISSIONS.ASSIGN_PERMISSIONS}
              >
                <Shield className="w-4 h-4 mr-2" />
                إدارة الصلاحيات
              </Button>
              {!role.isSystem && (
                <Button
                  variant="danger"
                  size="small"
                  onClick={() => handleDeleteRoleSuccess(role.id)}
                  className="w-full"
                  disabled={deleting}
                  permission={PERMISSIONS.DELETE_ROLES}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  {deleting ? 'جاري الحذف...' : 'حذف الدور'}
                </Button>
              )}
            </div>
          </Card>
        ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loading && filteredRoles.length === 0 && (
        <Card className="p-12 text-center">
          <UserCog className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <AppCardTitle className="mb-2">لا توجد أدوار</AppCardTitle>
          <AppText className="text-gray-600 mb-4">
            لم يتم إنشاء أي أدوار بعد
          </AppText>
          <Button
            variant="primary"
            onClick={handleCreateRoleClick}
            permission={PERMISSIONS.CREATE_ROLES}
          >
            <Plus className="w-4 h-4 mr-2" />
            إنشاء أول دور
          </Button>
        </Card>
      )}


      {/* Create Role Modal */}
      <CreateRoleModal
        open={isCreateRoleModalOpen}
        onClose={handleCloseCreateRoleModal}
        onSubmit={handleCreateRoleSubmit}
        loading={creating}
      />


      {/* Role Permissions Modal */}
      <RolePermissionsModal
        open={isPermissionsModalOpen}
        onClose={() => {
          closePermissionsModal();
          setManagingPermissionsRole(null);
        }}
        role={managingPermissionsRole}
        onPermissionsUpdated={handlePermissionsUpdated}
      />
    </div>
  );
};
